/* eslint-disable @typescript-eslint/no-unsafe-call */
import { Component, EventEmitter, Input, OnInit, Output, ViewChild } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { NotificationService } from 'cre-ng';
import dayjs from 'dayjs';

import { DayDetail, LeadQuestions } from '@app/core/model/qualifications.model';
import { DaysPanelComponent } from '@app/shared/days-panel/days-panel.component';
import { AuthService } from '@app/core/auth.service';
import { DailyFormResponse, CommitmentResponses } from './wig-daily-dynamic-commitment-form.model';
import { WigDailyDynamicCommitmentFormService } from './wig-daily-dynamic-commitment-form.service';

@Component({
  selector: 'app-wig-daily-dynamic-commitment-form',
  templateUrl: './wig-daily-dynamic-commitment-form.component.html',
  styleUrls: ['./wig-daily-dynamic-commitment-form.component.css'],
})
export class WigDailyCommitmentFormComponent implements OnInit {
  @Input() commitmentDetails: DayDetail[];
  @Output() formCompleted = new EventEmitter<boolean>();
  @Input() leadDescription: string;
  @Input() leadSubject: string;
  @Input() leadQuestionInformation: DayDetail;
  @Input() tractor: any;
  @Output() refreshStatsEvent = new EventEmitter<string>();
  @ViewChild('daysPanel') daysPanel: DaysPanelComponent;
  dailyCommitmentForm: FormGroup;
  driverCode: string;
  isFormSubmitted: boolean;
  isPSForm: boolean;
  questions: LeadQuestions;
  selectedDay: DayDetail;
  statusCanResponse = 'can respond';
  finished: boolean;
  errors: any[];
  tractorId: number;

  constructor(
    private formBuilder: FormBuilder,
    private wigDailyCommitmentFormService: WigDailyDynamicCommitmentFormService,
    private authService: AuthService,
    private notificationService: NotificationService
  ) {
    this.commitmentDetails = [];
    this.isFormSubmitted = false;
    this.leadDescription = '';
    this.leadSubject = '';
    this.selectedDay = null;
    this.leadQuestionInformation = null;
    this.finished = false;
    this.errors = [];
  }

  // #region GETS
  get commitmentResponse(): any {
    return this.dailyCommitmentForm.get('commitmentResponse');
  }

  refreshStatsCall() {
    this.refreshStatsEvent.emit();
  }

  // #end region GETS
  canSubmit(): boolean {
    let isSelectedDay = false;

    if (this.selectedDay !== null) {
      if (!this.selectedDay.disabled) {
        isSelectedDay = this.selectedDay.selected;
      }
    }

    if (isSelectedDay === false) {
      if (this.leadQuestionInformation.isDayOf) {
        isSelectedDay = true;
      }
    }

    const passValidation = this.dailyCommitmentForm.valid && this.checkValidations();

    return (
      this.dailyCommitmentForm.valid &&
      !this.isFormSubmitted &&
      isSelectedDay &&
      !this.finished &&
      passValidation
    );
  }

  canUseForm(): boolean {
    let isSelectedDay = false;

    if (this.selectedDay !== null) {
      if (!this.selectedDay.disabled) {
        isSelectedDay = this.selectedDay.selected;
      }
    }

    if (this.leadQuestionInformation.isDayOf) {
      isSelectedDay = true;
    }

    return !this.isFormSubmitted && isSelectedDay;
  }

  getFormName(commitmentName) {
    switch (commitmentName) {
      case 'DG Commitments?':
        return 'WIGDollarGeneralCommitment';
      case 'DT_FD Commitments?':
        return 'WIGDollarTreeFamilyDollarCommitment';
      case 'Dollar Weather App?':
        return 'WIGDollarWeatherApp';
      default:
        return '';
    }
  }

  getSelectedDay(day: DayDetail) {
    this.selectedDay = day;
  }

  initDailyCommitmentForm() {
    this.isPSForm = this.getFormName(this.leadSubject) !== '';
    if (this.tractor && this.tractor.tractor) {
      this.tractorId = this.tractor.tractor;
    }

    const form = this.formBuilder.group({});
    let count = 0;
    this.questions.questions.forEach((question) => {
      question.index = count;
      if (question.questionType === 'ThreeDaysDriveHours') {
        form.addControl(
          'control-day1-' + count.toString(),
          new FormControl('', [Validators.required])
        );
        form.addControl(
          'control-day2-' + count.toString(),
          new FormControl('', [Validators.required])
        );
        form.addControl(
          'control-day3-' + count.toString(),
          new FormControl('', [Validators.required])
        );
      } else if (question.questionType === 'MultipleSelect') {
        form.addControl('control-' + count.toString(), new FormControl('', [Validators.required]));
        let countResponse = 0;
        question.possibleResponses.sort().forEach((response) => {
          form.addControl(
            'control-' + count.toString() + '-response-' + countResponse.toString(),
            new FormControl('')
          );
          countResponse++;
        });
      } else {
        form.addControl('control-' + count.toString(), new FormControl('', [Validators.required]));
      }

      count++;
    });

    this.dailyCommitmentForm = form;
  }

  onSelect(i: number): void {
    const result = this.dailyCommitmentForm.get('control-' + i.toString()).value;
    this.dailyCommitmentForm.controls['control-' + i.toString()].reset();
    this.dailyCommitmentForm.controls['control-' + i.toString()].setValue(result);
  }

  onSelectCheck(i: number, isChecked: any, response: string): void {
    this.dailyCommitmentForm.get('control-' + i.toString()).reset();
    const question = this.leadQuestionInformation.leadQuestions.questions.find(
      (x) => x.index === i
    );

    let responseForControl = '';
    let countResponse = 0;
    question.possibleResponses.sort().forEach((questionResponse) => {
      const responseValue = this.dailyCommitmentForm.get(
        'control-' + i.toString() + '-response-' + countResponse.toString()
      ).value;

      if (responseValue.toString() !== '') {
        if (responseForControl.toString() !== '') {
          // eslint-disable-next-line @typescript-eslint/restrict-plus-operands
          responseForControl = responseForControl.toString() + ',' + responseValue.toString();
        } else {
          responseForControl = responseValue;
        }
      }
      countResponse++;
    });
    this.dailyCommitmentForm.get('control-' + i.toString()).setValue(responseForControl);
  }

  ngOnInit() {
    this.driverCode = this.authService.getCurrentUser().username;
    this.questions = this.leadQuestionInformation.leadQuestions;
    this.initDailyCommitmentForm();
  }

  submitDailyCommitmentForm(): void {
    const formName = this.getFormName(this.leadSubject);

    const selectedDayToSave = this.commitmentDetails.find(
      (detail) =>
        detail.status.toLowerCase() === this.statusCanResponse &&
        this.selectedDay?.date === detail.date
    );

    if (selectedDayToSave) {
      this.leadQuestionInformation = selectedDayToSave;
    }

    const response = new CommitmentResponses();
    if (formName) {
      const psFormReply: DailyFormResponse = {
        answer: 'Y',
        driverId: this.driverCode,
        dutyDate: this.selectedDay.date,
        formId: formName,
      };

      response.psFormReply = psFormReply;
    }
    response.leadId = this.leadQuestionInformation.leadId;
    response.driverCode = this.driverCode;

    if (this.tractorId) {
      response.tractor = this.tractorId.toString();
    }

    if (this.selectedDay && this.selectedDay.driverDailyLeadId > 0) {
      response.driverDailyLeadId = this.selectedDay.driverDailyLeadId;
      response.date = dayjs(this.selectedDay.date).format('YYYY-MM-DDTHH:mm:ss');
    } else {
      response.driverDailyLeadId = 0;
      if (this.leadQuestionInformation.isDayOf) {
        response.date = dayjs(Date.now()).format('YYYY-MM-DDTHH:mm:ss');
      }
    }

    response.responses = [];
    this.questions.questions.forEach((question) => {
      const questionText = question.question;
      const questionTypeChecking = question.questionType;
      let questionResponse = '';

      if (question.questionType === 'Date') {
        const responseForDateQuestion = this.dailyCommitmentForm
          .get('control-' + question.index.toString())
          .value.toString();

        questionResponse = dayjs(responseForDateQuestion).format('YYYY-MM-DDTHH:mm:ss');
      } else if (question.questionType === 'ThreeDaysDriveHours') {
        let responseThreeDays: string;

        responseThreeDays = this.dailyCommitmentForm
          .get('control-day1-' + question.index.toString())
          .value.toString();

        // eslint-disable-next-line @typescript-eslint/restrict-plus-operands
        responseThreeDays =
          // eslint-disable-next-line @typescript-eslint/restrict-plus-operands
          responseThreeDays +
          ',' +
          this.dailyCommitmentForm
            .get('control-day2-' + question.index.toString())
            .value.toString();

        responseThreeDays =
          // eslint-disable-next-line @typescript-eslint/restrict-plus-operands, @typescript-eslint/no-unused-vars
          responseThreeDays +
          ',' +
          this.dailyCommitmentForm
            .get('control-day3-' + question.index.toString())
            .value.toString();

        questionResponse = responseThreeDays;
      } else {
        const responseForNormalQuestion = this.dailyCommitmentForm
          .get('control-' + question.index.toString())
          .value.toString();

        questionResponse = responseForNormalQuestion;
      }
      response.responses.push({
        question: questionText,
        questionType: questionTypeChecking,
        questionAnswer: questionResponse,
      });
    });

    // General response
    let generalResponse = 'N';
    if (response.responses.length === 1) {
      if (response.responses[0].questionType === 'Yes/No') {
        if (response.responses[0].questionAnswer === 'Y') {
          generalResponse = 'Y';
        } else {
          generalResponse = 'N';
        }
      } else {
        generalResponse = 'N';
      }
    } else {
      generalResponse = 'Y';
    }

    this.wigDailyCommitmentFormService
      .responseDailyCommitment(response)
      .then(() => {
        if (this.selectedDay) {
          const updatedDay = this.daysPanel.updateDayStatusByResponse(
            this.selectedDay,
            generalResponse
          );
          this.commitmentDetails[this.selectedDay.index] = updatedDay;
          this.onSucess(response);
        } else {
          this.formCompleted.emit(true);
          this.refreshStatsCall();
          this.notificationService.showSuccess('Success', 'Successful response.');
          this.finished = true;
        }
      })
      .catch((error) => {
        if (error.error != null) {
          if (error.error.length > 0) {
            if (error.error.forEach) {
              // eslint-disable-next-line @typescript-eslint/no-shadow
              error.error.forEach((error: string) => {
                this.notificationService.showError('Error Responding', error);
              });
            } else {
              this.notificationService.showError('Error Responding', error.error);
            }
          }
        }
        this.isFormSubmitted = false;
      })
      .finally(() => {
        this.isFormSubmitted = false;
      });
  }

  public checkValidations(): boolean {
    const checkingErrors = [];

    // These ids are Daily5
    if (
      (this.dailyCommitmentForm.valid && this.leadQuestionInformation.leadId === 1) ||
      this.leadQuestionInformation.leadId === 7
    ) {
      const appt = this.dailyCommitmentForm.get('control-0').value;
      const eta = this.dailyCommitmentForm.get('control-2').value;
      const pta = this.dailyCommitmentForm.get('control-3').value;
      const day1 = this.dailyCommitmentForm.get('control-day1-4').value;
      const day2 = this.dailyCommitmentForm.get('control-day2-4').value;
      const day3 = this.dailyCommitmentForm.get('control-day3-4').value;

      if (eta < new Date()) {
        checkingErrors.push({
          severity: 'error',
          detail: 'Please pick dates/times that are not in the past.',
        });
      }

      if (pta <= eta) {
        checkingErrors.push({
          severity: 'error',
          detail: 'The PTA needs a date/time that is after the ETA.',
        });
      }

      if (day1 < 0 || day2 < 0 || day3 < 0) {
        checkingErrors.push({
          severity: 'error',
          detail: 'Drive hours must be greater than zero.',
        });
      }

      if (day1 % 1 > 0 || day2 % 1 > 0 || day3 % 1 > 0) {
        checkingErrors.push({
          severity: 'error',
          detail: 'Drive hours may not have a decimal.',
        });
      }
    }

    if (checkingErrors.length === 0) {
      this.errors = [];
      return true;
    } else if (
      checkingErrors.length !== this.errors.length ||
      (checkingErrors.length === this.errors.length &&
        !this.AreErrorsEqual(checkingErrors, this.errors))
    ) {
      this.errors = [];
      checkingErrors.forEach((errorMessage) => {
        this.errors.push(errorMessage);
      });
    }

    return false;
  }

  public AreErrorsEqual(checking: any[], values: any[]): boolean {
    checking = checking.sort();
    values = values.sort();
    let checkingString = '';
    let valuesString = '';
    checking.forEach((errorMessage) => {
      // eslint-disable-next-line @typescript-eslint/restrict-plus-operands
      checkingString = checkingString + errorMessage.detail.toString();
    });
    values.forEach((errorMessage) => {
      // eslint-disable-next-line @typescript-eslint/restrict-plus-operands
      valuesString = valuesString + errorMessage.detail.toString();
    });
    if (checkingString === valuesString) {
      return true;
    }
    return false;
  }

  private onSucess(response) {
    const dayToResponse = this.commitmentDetails.find(
      (detail) =>
        detail.status.toLowerCase() === this.statusCanResponse && detail.date !== response.date
    );

    this.resetCustomerForm();

    if (dayToResponse === undefined) {
      this.formCompleted.emit(true);
    }
    this.refreshStatsCall();
    this.notificationService.showSuccess('Success', 'Successful response.');
  }

  private resetCustomerForm(): void {
    this.dailyCommitmentForm.reset({
      dailyCommitmentForm: '',
    });
  }
}
